import { useCallback, useState } from 'react';
import {
  lireDocumentImporte,
  MAX_CAPTURES_FICHE_BYTES,
  MAX_FICHE_PAGES,
  MAX_FICHE_TEXTE,
  pickCapturesFiche,
  pickDocumentFile,
  pickImages,
  type PickedFile,
  type PickedImage,
} from '../services/mediaPicker';
import type { CvOfferInput } from '../services/claude';
import { octetsBase64, pagesPdf } from '../utils/pdfEtOctets';
import { t } from '../i18n';

export interface FicheDePosteInitiale {
  text?: string;
  file?: PickedFile | null;
  images?: PickedImage[];
}

export interface OptionsFicheDePoste {
  initiale?: FicheDePosteInitiale;
  /** Reçoit null au début de chaque choix, puis le message d'un sélecteur en échec. */
  onErreur: (message: string | null) => void;
  /** Une sélection de captures vide ne touche à rien (Interview) au lieu d'effacer le fichier (CvScore). */
  capturesVidesIgnorees?: boolean;
  /**
   * « Optimiser mon CV » (1.1.0) : captures, fichier et texte se CUMULENT en une seule fiche ; les
   * captures s'ajoutent (5 au plus, 8 Mo au total après compression), le texte s'arrête à 10 000
   * caractères, un PDF de plus de 20 pages est refusé.
   */
  cumul?: boolean;
}

const totalCaptures = (images: PickedImage[]) => images.reduce((n, i) => n + octetsBase64(i.data), 0);

/**
 * La fiche de poste facultative : texte collé, PDF ou Word, captures. Hors cumul, un seul mode à la
 * fois côté fichier et captures ; à l'envoi, captures > fichier > texte : reprise À L'IDENTIQUE des
 * copies de CvScoreScreen et InterviewScreen (08/10/2026). En cumul, « Optimiser mon CV ».
 */
export function useFicheDePoste({ initiale, onErreur, capturesVidesIgnorees = false, cumul = false }: OptionsFicheDePoste) {
  const [texte, setTexteBrut] = useState(initiale?.text ?? '');
  const setTexte = useCallback((v: string) => setTexteBrut(cumul ? v.slice(0, MAX_FICHE_TEXTE) : v), [cumul]);
  const [fichier, setFichier] = useState<PickedFile | null>(initiale?.file ?? null);
  const [captures, setCaptures] = useState<PickedImage[]>(initiale?.images ?? []);

  const choisirFichier = useCallback(async () => {
    onErreur(null);
    const outcome = await pickDocumentFile();
    if (outcome.canceled) return;
    if (outcome.error) return onErreur(outcome.error);
    if (outcome.file) {
      if (!cumul) setCaptures([]); // un seul mode d'offre à la fois, sauf en cumul
      setFichier(outcome.file);
    }
  }, [onErreur, cumul]);

  const ajouterCaptures = useCallback(async () => {
    onErreur(null);
    if (cumul) {
      const outcome = await pickCapturesFiche(captures.length);
      if (outcome.canceled) return;
      if (outcome.error) onErreur(outcome.error);
      if (!outcome.images || outcome.images.length === 0) return;
      const toutes = [...captures, ...outcome.images];
      if (totalCaptures(toutes) > MAX_CAPTURES_FICHE_BYTES) return onErreur(t('ficheDePoste.erreurs.tropLourd'));
      setCaptures(toutes);
      return;
    }
    const outcome = await pickImages();
    if (outcome.canceled) return;
    // Le sélecteur peut rendre des images ET une erreur (une image trop lourde parmi d'autres).
    if (outcome.error) onErreur(outcome.error);
    if (outcome.images && (!capturesVidesIgnorees || outcome.images.length > 0)) {
      setFichier(null);
      setCaptures(outcome.images);
    }
  }, [onErreur, capturesVidesIgnorees, cumul, captures]);

  const retirerCapture = useCallback((index: number) => setCaptures((avant) => avant.filter((_, i) => i !== index)), []);
  const retirerFichier = useCallback(() => setFichier(null), []);

  /** Fiche → charge utile, ou undefined si rien. Lève si le fichier ne se lit pas (ou dépasse les limites, en cumul). */
  const construire = useCallback(async (): Promise<CvOfferInput | undefined> => {
    if (cumul) {
      if (totalCaptures(captures) > MAX_CAPTURES_FICHE_BYTES) throw new Error(t('ficheDePoste.erreurs.tropLourd'));
      let document: { pdf?: { data: string }; docx?: { data: string } } = {};
      if (fichier) {
        const { payload, error } = await lireDocumentImporte(fichier);
        if (!payload) throw new Error(error);
        if ('pdf' in payload && payload.pdf && pagesPdf(payload.pdf.data) > MAX_FICHE_PAGES) throw new Error(t('ficheDePoste.erreurs.pdfTropLong'));
        document = payload;
      }
      const fiche: CvOfferInput = {
        ...(captures.length > 0 ? { images: captures.map(({ media_type, data }) => ({ media_type, data })) } : {}),
        ...document,
        ...(texte.trim() ? { text: texte.trim() } : {}),
      };
      return Object.keys(fiche).length > 0 ? fiche : undefined;
    }
    if (captures.length > 0) return { images: captures.map(({ media_type, data }) => ({ media_type, data })) };
    if (fichier) {
      const { payload, error } = await lireDocumentImporte(fichier);
      if (!payload) throw new Error(error);
      return payload;
    }
    if (texte.trim()) return { text: texte.trim() };
    return undefined;
  }, [captures, fichier, texte, cumul]);

  const vider = useCallback(() => {
    setTexteBrut('');
    setFichier(null);
    setCaptures([]);
  }, []);

  return {
    texte,
    setTexte,
    fichier,
    setFichier,
    captures,
    setCaptures,
    presente: captures.length > 0 || fichier !== null || texte.trim().length > 0,
    choisirFichier,
    ajouterCaptures,
    construire,
    vider,
    retirerCapture,
    retirerFichier,
  };
}

export type FicheDePoste = ReturnType<typeof useFicheDePoste>;
